'use client';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import {styled} from '@mui/material/styles';
import {useState, type ElementType, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaMenuIcon} from '../../icon';
import {CloseButton, IconButton} from '../../primitives';

interface MobileMenuProps {
  anchor?: 'left' | 'right';
  logoSlot?: ReactNode;
  cta?: ReactNode;
  children: ReactNode;
}

const MenuPanel = styled(Box)<{component?: ElementType}>(({theme}) => ({
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  // Dark drawer matching the header/footer chrome (Figma mobile menu = #222).
  backgroundColor: theme.palette.colors.surface.header.default,
  color: theme.palette.colors.text.neutral.inverse,
  height: '100%',
  overflow: 'hidden',
}));

const DrawerHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2, 2, 3),
}));

const NavList = styled(Box)(({theme}) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const BottomSticky = styled(Box)(({theme}) => ({
  position: 'sticky',
  bottom: 0,
  padding: 0,
  background: theme.palette.colors.surface.header.default,
}));

const CtaCard = styled(Box)(({theme}) => ({
  borderTopLeftRadius: theme.radii.xl,
  borderTopRightRadius: theme.radii.xl,
  padding: theme.spacing(2),
  background: theme.palette.colors.surface.neutral.inverseMuted,
}));

export function MobileMenu({anchor = 'right', logoSlot, cta, children}: MobileMenuProps) {
  const [open, setOpen] = useState(false);
  const {t} = useTranslation();

  return (
    <>
      <IconButton
        aria-label={t('menu')}
        aria-expanded={open}
        onClick={() => {
          setOpen(true);
        }}
        sx={theme => ({color: theme.palette.colors.text.neutral.inverse})}
      >
        <AppIcon icon={FaMenuIcon} sx={{fontSize: '19px'}} aria-hidden />
      </IconButton>
      <Drawer
        anchor={anchor}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <MenuPanel
          component="nav"
          onClick={event => {
            // Any navigation link (nav items, country/project links, View All, logo)
            // dismisses the drawer. The accordion toggle and the Register Interest
            // CTA are <button>s, so they intentionally leave it open.
            if ((event.target as HTMLElement).closest('a')) {
              setOpen(false);
            }
          }}
        >
          <DrawerHeader>
            <Box>{logoSlot}</Box>
            <CloseButton
              onClick={() => {
                setOpen(false);
              }}
              sx={theme => ({color: theme.palette.colors.text.neutral.inverse})}
            />
          </DrawerHeader>
          <NavList>{children}</NavList>
          {cta && (
            <BottomSticky>
              <CtaCard>{cta}</CtaCard>
            </BottomSticky>
          )}
        </MenuPanel>
      </Drawer>
    </>
  );
}

MobileMenu.displayName = 'MobileMenu';
